import clsx from 'clsx';
import { TextInput, ThemeContext, ThemeType } from 'grommet';
import _ from 'lodash-es';
import { SearchIcon } from 'lucide-preact';
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';

import HabitatStatus from '@/components/HabitatCard/HabitatStatus';
import LiveBadge from '@/components/LiveBadge';
import { searchHabitats } from '@/helpers/habitatsSearch';
import { HabitatListItem } from '@/queries/habitats/types';
import { useSelector } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';
import { trackMP } from '@/shared/mp';

const theme = {
  global: {
    drop: {
      shadowSize: 'medium',
      extend: `
        margin-top: 4px;
        padding-top: 8px;
        padding-bottom: 8px;
        border-radius: 12px;
        overflow: hidden;
      `,
    },
  },
  button: {
    extend: 'border-radius: 0;',
  },
} as ThemeType;

export type SearchBarHabitat = Pick<HabitatListItem, '_id' | 'trailer' | 'zoo' | 'slug'>;

interface EmbeddedHabitatSearchBarProps {
  placeholder: string;
  /**
   * Where a result goes. Defaults to the habitat's trailer page in a new tab; hosts that are
   * themselves a media page override it, since a new tab is not what a result means there.
   */
  onSelectHabitat?: (habitat: SearchBarHabitat) => void;
}

const EmbeddedHabitatSearchBar = ({ placeholder, onSelectHabitat }: EmbeddedHabitatSearchBarProps) => {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const selectSortedHabitats = useMemo(() => createSortedHabitatsSelector(), []);
  const sortedHabitats = useSelector(selectSortedHabitats);

  const onHabitatClick = useCallback(
    (habitat: SearchBarHabitat) => {
      const { _id, trailer, zoo, slug } = habitat;
      setSearchValue('');
      if (onSelectHabitat) {
        onSelectHabitat(habitat);
      } else {
        window.open(trailer ? `/media/${trailer.slugPrefix || 'media'}-${trailer._id}` : `/h/${zoo.slug}/${slug}`);
      }
      trackMP('widget-search-result-clicked', {
        habitatId: _id,
      });
    },
    [onSelectHabitat],
  );

  const suggestions = useMemo(() => {
    const input = searchValue.trim().toLowerCase();

    return searchHabitats(_.chain(sortedHabitats), input)
      .slice(0, 5)
      .map(([{ _id, profileImage, animal, isStreamOn, liveTalk, trailer, zoo, slug }], ind, list) => ({
        value: _id,
        onClick: () => {
          onHabitatClick({ _id, trailer, zoo, slug });
        },
        label: (
          <div
            className={clsx(
              'preflight flex items-center gap-1.5 px-4 py-2.5',
              ind < list.length - 1 ? 'border-b border-stone-200' : '',
            )}
          >
            <div className="shrink-0">
              <img
                src={profileImage}
                alt={animal}
                className="preflight preflight-img block size-[30px] rounded-full bg-stone-200 object-cover"
              />
            </div>
            <span className="line-clamp-2 min-w-0 grow text-sm font-medium">{animal}</span>
            <HabitatStatus className="shrink-0" online={isStreamOn} liveTalk={liveTalk} />
          </div>
        ),
      }))
      .value();
  }, [searchValue, sortedHabitats, onHabitatClick]);

  const onSelect = ({ suggestion }: { suggestion: { onClick?: () => void } }) => {
    suggestion.onClick?.();
    boxRef.current?.querySelector('input')?.blur();
  };

  return (
    <ThemeContext.Extend value={theme}>
      <div
        className="preflight flex w-full items-center gap-2 rounded-full border border-[#D9D9D9] bg-white px-4 py-2 text-sm text-black md:w-96"
        ref={boxRef}
      >
        <SearchIcon className="size-4 shrink-0 text-[#78716C]" />
        <LiveBadge className="shrink-0" />
        <TextInput
          plain
          name="search-habitat"
          className="!placeholder:text-stone-500 !grow text-ellipsis !p-0 !font-normal"
          autoComplete="off"
          dropHeight="medium"
          dropTarget={boxRef.current || undefined}
          dropProps={{
            ref: (element) => {
              const rect = boxRef.current?.getBoundingClientRect();
              if (element && rect) {
                // eslint-disable-next-line no-param-reassign
                element.style.width = `${rect.width}px`;
                // eslint-disable-next-line no-param-reassign
                element.style.left = `${rect.left}px`;
              }
            },
          }}
          placeholder={placeholder}
          suggestions={suggestions}
          value={searchValue}
          onChange={({ currentTarget }) => setSearchValue(currentTarget.value)}
          onSelect={onSelect}
          onKeyDown={(e) => {
            // Only trigger when there is only one result and the user presses enter
            // If there are more than one result, the user can use the up and down arrows to select the desired result
            if (e.key === 'Enter' && suggestions.length === 1) {
              onSelect({ suggestion: suggestions[0] });
            }
          }}
        />
      </div>
    </ThemeContext.Extend>
  );
};

export default EmbeddedHabitatSearchBar;
